import { api } from "./api";

const SW_PATH = "/sw.js";
const DISMISS_KEY = "lendly:push-prompt-dismissed";

const urlB64ToUint8Array = (base64String) => {
    // Web Push subscribe() expects a Uint8Array, not a URL-safe base64 string.
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(base64);
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export const pushSupported = () =>
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

export const pushPermission = () =>
    typeof Notification !== "undefined" ? Notification.permission : "default";

const registerSw = async () => {
    // Register (or reuse) the service worker. On iOS PWA installs the
    // scope must be root ("/") for the push subscription to survive
    // "Add to Home Screen".
    const existing = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (existing) return existing;
    return navigator.serviceWorker.register(SW_PATH);
};

const fetchPublicKey = async () => {
    const { data } = await api.get("/push/public-key");
    if (!data?.enabled || !data?.public_key) {
        throw new Error("Push not configured on server");
    }
    return data.public_key;
};

/**
 * Request permission (if not already granted) and register a push
 * subscription with the backend for the current user. Returns the
 * PushSubscription on success.
 */
export const enablePush = async () => {
    if (!pushSupported()) throw new Error("Push not supported on this device");

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
        throw new Error(permission === "denied" ? "denied" : "dismissed");
    }

    const registration = await registerSw();
    // Wait until the SW is active before subscribing (Safari is strict here).
    if (!registration.active) {
        await new Promise((resolve) => {
            const sw = registration.installing || registration.waiting;
            if (!sw) return resolve();
            sw.addEventListener("statechange", () => {
                if (sw.state === "activated") resolve();
            });
        });
    }

    const publicKey = await fetchPublicKey();
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
        subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlB64ToUint8Array(publicKey),
        });
    }

    const raw = subscription.toJSON();
    await api.post("/push/subscribe", {
        endpoint: raw.endpoint,
        keys: raw.keys,
        user_agent: navigator.userAgent,
    });

    return subscription;
};

/**
 * Revoke the current browser's push subscription and remove it from the
 * server. Safe to call even if no subscription exists.
 */
export const disablePush = async () => {
    if (!pushSupported()) return;
    const registration = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (!registration) return;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;
    try {
        await api.post("/push/unsubscribe", { endpoint: subscription.endpoint });
    } catch (_err) {
        // Ignore — we still unsubscribe locally.
    }
    await subscription.unsubscribe();
};

export const isSubscribed = async () => {
    if (!pushSupported()) return false;
    const registration = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (!registration) return false;
    const sub = await registration.pushManager.getSubscription();
    return !!sub;
};

export const dismissPushPrompt = () => {
    try {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (_err) {}
};

export const isPushPromptDismissed = () => {
    try {
        const at = localStorage.getItem(DISMISS_KEY);
        if (!at) return false;
        // Re-prompt after 30 days so users who dismiss aren't locked out
        // of push forever.
        return Date.now() - Number(at) < 1000 * 60 * 60 * 24 * 30;
    } catch (_err) {
        return false;
    }
};
